// src/features/analytics/useAnalyticsMediaAudience.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import {
  ANALYTICS_QUERY_KEYS,
  type AnalyticsMediaAudienceDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, AudienceData } from './types';

export interface UseAnalyticsMediaAudienceResult {
  audience: AnalyticsEnvelope<AudienceData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Per-media audience breakdown (geo + embedDomain) for the Content-tab
 * drill-down Drawer — account scope only. Pass null cid or mediaId to disable
 * (the Drawer keeps the query enabled only while open, matching the legacy
 * mediasdetail drawer pattern).
 */
export function useAnalyticsMediaAudience(
  cid: number | null,
  mediaId: string | null,
  dimension: AnalyticsMediaAudienceDimension,
  range: UtcRange,
  metric: AnalyticsMetric
): UseAnalyticsMediaAudienceResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.mediaAudience(
      cid ?? 0,
      mediaId ?? '',
      range.from,
      range.to,
      dimension,
      metric
    ),
    queryFn: () => AnalyticsApi.getMediaAudience(cid!, mediaId!, dimension, range, metric),
    enabled: cid !== null && mediaId !== null,
    staleTime: 60_000,
  });

  return {
    audience: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
